import { Component, inject, Inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SnackbarService } from '../../../../services/snackbar.service';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ValidationService } from '../../../../core/common/utils/validation';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { CheckService } from '../../../../core/checks/check';
import { CurrentUserService } from '../../../../services/current-user';
import { ProjectService } from '../../../../core/projects/project';
import { User } from '../../../../core/models/user-model';

// Hinweis: Beim Öffnen des Dialogs muss die projectCode übergeben werden, z.B.:
// this.dialog.open(NewCheckDialog, { data: { projectCode: <CODE> } });
@Component({
  selector: 'app-new-check-dialog',
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatChipsModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    CommonModule,
    MatDatepickerModule,
    MatButtonToggleModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './new-check-dialog.html',
  styleUrl: './new-check-dialog.scss',
  standalone: true,
})
export class NewCheckDialog {
  readonly dialog = inject(MatDialogRef<NewCheckDialog>)
  readonly snackbarService = inject(SnackbarService);
  readonly fb = inject(FormBuilder);
  private readonly checkService = inject(CheckService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly projectService = inject(ProjectService);

  checkForm: FormGroup;

  /** Steuert welches Pfad-Feld aktiv ist: 'sharepoint' oder 'directoryP' */
  readonly sourceType = signal<'sharepoint' | 'directoryP'>('sharepoint');
  readonly isSaving = signal(false);

  // Project-ID aus MAT_DIALOG_DATA
  constructor(@Inject(MAT_DIALOG_DATA) public data: { projectCode: string }) {
    this.checkForm = this.fb.group({
      displayName: ['', [Validators.required, Validators.minLength(3)]],
      sharepointFolderPath: ['', [ValidationService.urlValidator()]],
      directoryPFolderPath: [{ value: '', disabled: true }],
      dueDate: ['', [Validators.required]]
    }, {
      validators: [ValidationService.exclusivePathValidator()]
    });
  }

  /** Wechselt den Speicherort-Typ und disabled/enabled die FormControls entsprechend */
  onSourceTypeChange(type: 'sharepoint' | 'directoryP'): void {
    this.sourceType.set(type);
    if (type === 'sharepoint') {
      this.checkForm.get('directoryPFolderPath')?.setValue('');
      this.checkForm.get('directoryPFolderPath')?.disable();
      this.checkForm.get('sharepointFolderPath')?.enable();
    } else {
      this.checkForm.get('sharepointFolderPath')?.setValue('');
      this.checkForm.get('sharepointFolderPath')?.disable();
      this.checkForm.get('directoryPFolderPath')?.enable();
    }
  }

  get projectCode() {
    return this.data?.projectCode ?? null;
  }

  closeDialog(): void {
    this.dialog.close();
  }

  getErrorMessage(fieldName: string): string {
    const control = this.checkForm.get(fieldName);
    if (!control) return '';

    if (control.hasError('required')) {
      return 'Dieses Feld ist erforderlich';
    }
    if (control.hasError('minlength')) {
      const minLength = control.getError('minlength').requiredLength;
      return `Mindestens ${minLength} Zeichen erforderlich`;
    }
    if (control.hasError('invalidUrl')) {
      return 'Bitte eine gültige URL eingeben (z. B. https://…)';
    }
    if (control.hasError('exclusivePath')) {
      return 'Bitte nur einen Speicherort angeben (SharePoint oder Laufwerk P).';
    }
    return '';
  }

  createCheck(): void {
    if (this.checkForm.invalid) {
      Object.keys(this.checkForm.controls).forEach(key => {
        this.checkForm.get(key)?.markAsTouched();
      });
      return;
    }

    const code = this.projectCode;
    if (!code) {
      this.snackbarService.error('Kein Projekt ausgewählt');
      return;
    }

    const userOid = this.currentUserService.currentUserOid();
    if (!userOid) {
      this.snackbarService.error('Kein Benutzer eingeloggt');
      return;
    }

    const formValue = this.checkForm.getRawValue();
    const dueDate1 = new Date(formValue.dueDate).toISOString();

    this.isSaving.set(true);

    // 1. POST /check
    this.checkService.createCheckApi({ description: formValue.displayName, doneAt: null }).pipe(
      // 2. PUT /check/{id}/status – initialer Stage-Setup (K1-1)
      switchMap(checkDto =>
        this.checkService.updateCheckStatusApi(checkDto.guid, {
          dueDate1,
          dueDate12: null,
          dueDate2: null,
          checkerOid1: userOid,
          checkerOid12: null,
          checkerOid2: null,
        }).pipe(
          // 3. GET /project/{code}/document – alle Dokumente des Projekts laden
          switchMap(stages =>
            this.projectService.getDocumentsByProjectApi(code).pipe(
              // 4. POST /check/{id}/document/{docGuid} – Check an jedes Dokument heften
              switchMap(docs => {
                const linkCalls = docs.map(doc =>
                  this.checkService.linkDocumentApi(checkDto.guid, doc.guid).pipe(
                    catchError((e: Error) => {
                      console.log(e.message);
                      return of(null)
                    }) // Einzelfehler ignorieren, Rest weiterführen
                  )
                );
                return (linkCalls.length > 0 ? forkJoin(linkCalls) : of([])).pipe(
                  switchMap(() => {
                    // 5. Lokal puffern & Dialog schließen
                    const enrichedDto = { ...checkDto, projectCode: code };
                    const project = this.projectService.projects().find(p => p.contractNr === code) ?? null;
                    if (project) {
                      // Aktuellen User mitgeben, damit creator & checker11 im lokalen Puffer
                      // sofort mit korrektem displayName befüllt sind (statt roher OID).
                      const currentUser = this.currentUserService.currentUser();
                      const usersByOid = currentUser
                        ? new Map<string, User>([[currentUser.oid, currentUser]])
                        : new Map<string, User>();
                      this.checkService.addCreatedCheck(enrichedDto, stages, project, usersByOid);
                    }
                    return of(checkDto.guid);
                  })
                );
              })
            )
          )
        )
      )
    ).subscribe({
      next: (guid) => {
        this.isSaving.set(false);
        this.snackbarService.success('Neue Kontrolle angelegt', {
          entries: [formValue.displayName, formValue.sharepointFolderPath, formValue.directoryPFolderPath, dueDate1]
            .filter(e => !!e && String(e).trim().length > 0)
        });
        this.dialog.close(guid);
      },
      error: (err) => {
        this.isSaving.set(false);
        console.error('Fehler beim Erstellen der Kontrolle:', err);
        this.snackbarService.error('Fehler beim Erstellen der Kontrolle');
      }
    });
  }
}
